import { useEffect, useRef, useMemo } from 'react';
import type * as Tone from 'tone';
import { getToneAudioEngine } from '../renderer/rendererBridge';

interface Props {
  params: Record<string, number>;
  /** Optional dedicated analyser node (e.g. from preview chain) — preferred over master meter */
  previewAnalyser?: Tone.Analyser | null;
}

/**
 * DaVinci Resolve Fairlight-style reverb visualizer.
 *
 * - Left: Three parallax SVG wireframe planes (room size)
 * - Right: Energy Decay Relief (EDR) graph — live FFT "decay mountain"
 *          fed by either the preview analyser or the master meter
 * - Bottom: Computed RT60, volume & pre-delay stats
 */
export function ReverbVisualizer({ params, previewAnalyser }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  const historyRef = useRef<Float32Array[]>([]);

  const roomSize = params.roomSize ?? 0.7;
  const brightness = params.brightness ?? 50;
  const preDelay = params.preDelay ?? 0.01;
  const modulation = params.modulation ?? 2;
  const loGain = params.loGain ?? 0;
  const hiGain = params.hiGain ?? 0;

  const computed = useMemo(() => {
    const vol = Math.round(20 + roomSize * 480);
    const absorption = 10 + (1 - roomSize) * 40 + ((100 - brightness) / 100) * 20;
    const rt60 = Math.max(0.1, (0.16 * vol) / absorption);

    const baseW = 100 + roomSize * 200;
    const baseH = 60 + roomSize * 100;
    const front = { x: 120, y: 70, w: baseW, h: baseH };
    const mid = {
      x: 120 - roomSize * 50,
      y: 70 - roomSize * 30,
      w: baseW * 0.7,
      h: baseH * 0.7,
    };
    const back = {
      x: 120 - roomSize * 80,
      y: 70 - roomSize * 50,
      w: baseW * 0.4,
      h: baseH * 0.4,
    };

    return { vol, rt60, front, mid, back };
  }, [roomSize, brightness]);

  // EDR canvas drawing loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const cw = rect.width;
    const ch = rect.height;

    const MAX_HISTORY = 6;

    function getAnalyserValues(): Float32Array | null {
      // Prefer the dedicated preview analyser
      if (previewAnalyser) {
        const v = previewAnalyser.getValue();
        if (v instanceof Float32Array && v.length > 0) return v;
      }
      // Fall back to master meter
      const engine = getToneAudioEngine();
      const analyser = engine?.getMasterMeterNode();
      const v = analyser?.getValue();
      if (v && v instanceof Float32Array && v.length > 0) return v;
      return null;
    }

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, cw, ch);

      const values = getAnalyserValues();

      if (values) {
        // Check if there's actual signal (not just silence)
        let maxSample = 0;
        for (let i = 0; i < values.length; i++) {
          const abs = Math.abs(values[i]);
          if (abs > maxSample) maxSample = abs;
        }

        if (maxSample > 0.001) {
          historyRef.current.push(new Float32Array(values));
          if (historyRef.current.length > MAX_HISTORY) {
            historyRef.current.shift();
          }
        } else if (historyRef.current.length > 0) {
          // Decay: fade out old frames gradually
          historyRef.current.shift();
        }
      }

      if (historyRef.current.length > 0) {
        drawLiveEDR(ctx, cw, ch);
      } else {
        drawSimulatedEDR(ctx, cw, ch);
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    function drawLiveEDR(c: CanvasRenderingContext2D, w: number, h: number) {
      const history = historyRef.current;
      for (let layer = 0; layer < history.length; layer++) {
        const layerData = history[layer];
        const age = (history.length - 1 - layer) / MAX_HISTORY;
        const alpha = 0.15 + (1 - age) * 0.65;
        const yOffset = age * h * 0.12;

        const gradient = c.createLinearGradient(0, yOffset, 0, h);
        gradient.addColorStop(0, `rgba(163, 112, 247, ${alpha * 0.9})`);
        gradient.addColorStop(0.4, `rgba(76, 201, 240, ${alpha * 0.5})`);
        gradient.addColorStop(1, `rgba(76, 201, 240, 0)`);

        c.beginPath();
        c.moveTo(0, h);

        const bufLen = layerData.length;
        for (let i = 1; i < bufLen; i++) {
          const x = (Math.log(i) / Math.log(bufLen)) * w;
          const sample = Math.abs(layerData[i]);
          const freqRatio = i / bufLen;
          const brightnessMult = freqRatio > 0.5
            ? 1 - (1 - brightness / 100) * (freqRatio - 0.5) * 2
            : 1;
          const loMult = freqRatio < 0.3 ? 1 + (loGain / 12) * 0.5 : 1;
          const hiMult = freqRatio > 0.7 ? 1 + (hiGain / 12) * 0.5 : 1;
          const y = h - sample * h * 3 * brightnessMult * loMult * hiMult - yOffset;
          c.lineTo(x, Math.max(yOffset, y));
        }

        c.lineTo(w, h);
        c.fillStyle = gradient;
        c.fill();

        if (layer === history.length - 1) {
          c.strokeStyle = `rgba(163, 112, 247, 0.7)`;
          c.lineWidth = 1.5;
          c.stroke();
        }
      }
    }

    function drawSimulatedEDR(c: CanvasRenderingContext2D, w: number, h: number) {
      const layers = 4;
      for (let layer = 0; layer < layers; layer++) {
        const age = layer / layers;
        const alpha = 0.7 - age * 0.5;
        const yShift = age * h * 0.1;

        const gradient = c.createLinearGradient(0, yShift, 0, h);
        gradient.addColorStop(0, `rgba(163, 112, 247, ${alpha * 0.8})`);
        gradient.addColorStop(0.5, `rgba(76, 201, 240, ${alpha * 0.4})`);
        gradient.addColorStop(1, 'rgba(76, 201, 240, 0)');

        c.beginPath();
        c.moveTo(0, h);

        const bins = 64;
        const decayRate = 3 / (computed.rt60 + 0.1);
        const timeOffset = age * 0.3;

        for (let i = 0; i < bins; i++) {
          const freqRatio = i / bins;
          const x = (Math.log(i + 1) / Math.log(bins + 1)) * w;

          let energy = Math.exp(-decayRate * (freqRatio * 0.5 + timeOffset)) * 0.7;

          if (freqRatio > 0.4) {
            energy *= 1 - (1 - brightness / 100) * (freqRatio - 0.4) / 0.6;
          }
          if (freqRatio < 0.3) energy *= 1 + (loGain / 12) * 0.4;
          if (freqRatio > 0.7) energy *= 1 + (hiGain / 12) * 0.4;

          const modWobble = modulation > 0
            ? Math.sin(i * 3.7 + layer * 2.1) * (modulation / 100) * 0.08 * energy
            : 0;
          const noise = (Math.sin(i * 11.3 + layer * 5.7) * 0.03
            + Math.sin(i * 7.1 + layer * 3.3) * 0.02) * energy;

          const y = h - (energy + modWobble + noise) * h * 0.85 - yShift;
          c.lineTo(x, Math.max(yShift + 2, y));
        }

        c.lineTo(w, h);
        c.fillStyle = gradient;
        c.fill();

        if (layer === 0) {
          c.strokeStyle = 'rgba(163, 112, 247, 0.5)';
          c.lineWidth = 1.5;
          c.stroke();
        }
      }
    }

    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, [roomSize, brightness, preDelay, modulation, loGain, hiGain, computed.rt60, previewAnalyser]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {/* SVG Wireframe Room */}
        <div className="w-[140px] h-[100px] shrink-0 rounded-md border border-[#2A2A2E] bg-[#111113] overflow-hidden">
          <svg viewBox="0 0 400 200" className="w-full h-full">
            <defs>
              <filter id="reverb-glow">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <rect
              x={computed.back.x} y={computed.back.y}
              width={computed.back.w} height={computed.back.h}
              fill="rgba(76, 201, 240, 0.03)" stroke="#4cc9f0"
              strokeWidth="1.5" strokeOpacity="0.25" rx="1"
              style={{ transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
            />
            <rect
              x={computed.mid.x} y={computed.mid.y}
              width={computed.mid.w} height={computed.mid.h}
              fill="rgba(76, 201, 240, 0.05)" stroke="#4cc9f0"
              strokeWidth="1.5" strokeOpacity="0.5" rx="1"
              style={{ transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
            />
            <rect
              x={computed.front.x} y={computed.front.y}
              width={computed.front.w} height={computed.front.h}
              fill="rgba(76, 201, 240, 0.07)" stroke="#4cc9f0"
              strokeWidth="1.5" strokeOpacity="0.9" rx="1"
              filter="url(#reverb-glow)"
              style={{ transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
            />
          </svg>
        </div>

        {/* EDR (Energy Decay Relief) Canvas */}
        <div className="flex-1 h-[100px] rounded-md border border-[#2A2A2E] bg-[#111113] overflow-hidden">
          <canvas ref={canvasRef} className="w-full h-full" />
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-4 px-1">
        <span className="text-[10px] text-[#636366] font-mono tabular-nums">
          Vol: <span className="text-[#8E8E93] font-medium">{computed.vol}m³</span>
        </span>
        <span className="text-[10px] text-[#636366] font-mono tabular-nums">
          RT60: <span className="text-[#8E8E93] font-medium">{computed.rt60.toFixed(1)}s</span>
        </span>
        <span className="text-[10px] text-[#636366] font-mono tabular-nums">
          Pre: <span className="text-[#8E8E93] font-medium">{(preDelay * 1000).toFixed(0)}ms</span>
        </span>
      </div>
    </div>
  );
}
